import React, { useEffect, useState } from 'react';
import Footer from '../Footer';
import DesktopHeader from '../Header/DesktopHeader';
import MobileHeader from '../Header/MobileHeader';
import BottomNav from '../BottomNav/BottomNav';
import SuggestionModal from '../SuggestionModal';
import PageTransition from '../PageTransition/PageTransition';
import { useAuth } from '../../contexts/AuthContext';
import { TabType, NavigationDirection } from '../../hooks/useNavigation';
import { useSwipeNavigation } from '../../hooks/useSwipeNavigation';
import { useScrollRestoration } from '../../hooks/useScrollRestoration';
import { submitSuggestion, SuggestionData } from '../../services/suggestionService';
import { TimerProvider } from '../../contexts/TimerContext';
import { PomodoroProvider } from '../../contexts/PomodoroContext';
import RunningTimerBar from '../RunningTimerBar/RunningTimerBar';
import './Layout.css';

interface LayoutProps {
  children: React.ReactNode;
  activeTab: TabType | null;
  onTabChange: (tab: TabType) => void;
  onAddGoal: () => void;
  overallProgress: number;
  completedGoalsCount: number;
  totalGoalsCount: number;
  navigationDirection: NavigationDirection;
  routeKey: string;
  goBack: () => void;
}

const Layout: React.FC<LayoutProps> = ({
  children,
  activeTab,
  onTabChange,
  onAddGoal,
  overallProgress,
  completedGoalsCount,
  totalGoalsCount,
  navigationDirection,
  routeKey,
  goBack,
}) => {
  const { state: authState, logout } = useAuth();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isSuggestionOpen, setIsSuggestionOpen] = useState(false);
  const swipeRef = useSwipeNavigation(goBack);
  useScrollRestoration(routeKey, navigationDirection);

  // Scroll event listener with throttle
  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const scrollTop = window.scrollY;
          
          // Scroll durumunu belirle
          setIsScrolled(scrollTop > 20);
          
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSuggestionSubmit = async (data: SuggestionData) => {
    await submitSuggestion(data);
  };

  return (
    <TimerProvider>
      <PomodoroProvider>
      <div className="app app-shell">
        {/* Desktop Header - hidden on mobile */}
        <div className="desktop-only">
          <DesktopHeader
            activeTab={activeTab}
            onTabChange={onTabChange}
            onAddGoal={onAddGoal}
            onOpenSuggestion={() => setIsSuggestionOpen(true)}
            overallProgress={overallProgress}
            completedGoalsCount={completedGoalsCount}
            totalGoalsCount={totalGoalsCount}
            isScrolled={isScrolled}
            user={authState.user}
            onLogout={logout}
          />
        </div>

        {/* Mobile Header - hidden on desktop */}
        <div className="mobile-only">
          <MobileHeader
            activeTab={activeTab}
            onTabChange={onTabChange}
            onOpenSuggestion={() => setIsSuggestionOpen(true)}
            overallProgress={overallProgress}
            completedGoalsCount={completedGoalsCount}
            totalGoalsCount={totalGoalsCount}
            isScrolled={isScrolled}
            user={authState.user}
            onLogout={logout}
          />
        </div>

        <main ref={swipeRef} className={`main-content ${isScrolled ? 'nav-fixed' : ''}`}>
          <PageTransition routeKey={routeKey} direction={navigationDirection}>
            {children}
          </PageTransition>
        </main>

        <Footer />

        <BottomNav activeTab={activeTab} onTabChange={onTabChange} onAddGoal={onAddGoal} />

        <RunningTimerBar hidden={activeTab === 'timely'} />

        {/* Suggestion Modal */}
        <SuggestionModal
          isOpen={isSuggestionOpen}
          onClose={() => setIsSuggestionOpen(false)}
          onSubmit={handleSuggestionSubmit}
        />
      </div>
      </PomodoroProvider>
    </TimerProvider>
  );
};

export default Layout;
